/**
 * Phase 1 workflows list view — Section 4 of the design doc.
 *
 * Layout: left scope rail + filter chips + search + compact table.
 *   - Scope rail counts come from /api/v1/workflows?scope=X&limit=1 per
 *     scope; we read the count from totalish-via-pagination (Phase 1 lists
 *     don't return a `total`, so we settle for "loaded count" with a
 *     "+ more" hint when nextCursor is non-null).
 *   - Search is server-side; 200ms debounce.
 *   - Filter chips: trigger kind (TIME/EVENT/CONDITION), status (DRAFT/ACTIVE/ARCHIVED),
 *     tag (server-side ?tag=). Trigger/Status are client-only filters over
 *     the loaded page since Phase 1 list endpoint doesn't accept them directly
 *     (an intentional MVP cut — server-side trigger filter is Phase 2).
 *   - Click row → navigate to /workflows/:id.
 *   - + New workflow → POST /workflows (empty draft) then navigate.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '../../lib/api.js';
import { useModulePermission, useAuth } from '../../lib/auth-context.js';
import { Button } from '../../ui/Button.js';
import { Badge, StatusDot } from '../../ui/Card.js';
import type {
  ScopeKey,
  ScopeRailEntry,
  WorkflowListResponse,
  WorkflowMeta,
} from './types.js';

const SCOPES: { key: ScopeKey; label: string; hint?: string }[] = [
  { key: 'mine', label: 'My workflows' },
  { key: 'admin-published', label: 'Org standard' },
  { key: 'peer-published', label: 'Shared by peers' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'archived', label: 'Archived' },
];

type TriggerFilter = 'all' | 'TIME' | 'EVENT' | 'CONDITION';
type StatusFilter = 'all' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

function useDebounced<T>(value: T, ms = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Workflow meta doesn't include triggers; the list endpoint returns the
 * meta only. We surface "Trigger" column as a textual hint derived from
 * a separate per-row fetch only when needed for the active filter. Phase 1
 * keeps it cheap by showing a dash when triggers aren't loaded yet.
 */

export function WorkflowsListView() {
  const navigate = useNavigate();
  const perm = useModulePermission('workflows');
  const qc = useQueryClient();
  const { me } = useAuth();
  const role = me?.user.role;

  // Delete a workflow. The backend enforces the policy (super_admin-only for
  // org-level rows; admins + author for custom). We mirror it in canDeleteRow
  // so the control only appears where it's allowed. Hard-delete is attempted;
  // rows with run history are archived server-side instead.
  const deleteWf = useMutation({
    mutationFn: (id: string) =>
      api<{ deleted?: boolean }>(`/api/v1/workflows/${id}?hard=1`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['workflows'] }),
    onError: (e: Error) => window.alert(e.message || 'Delete failed'),
  });
  const canDeleteRow = (wf: WorkflowMeta): boolean => {
    if (role === 'super_admin') return true;
    if (role === 'admin')
      return !(wf.visibility === 'ORG_PUBLISHED' || wf.visibility === 'ADMIN_PUBLISHED');
    return false;
  };
  const handleDelete = (wf: WorkflowMeta) => {
    const orgLevel =
      wf.visibility === 'ORG_PUBLISHED' || wf.visibility === 'ADMIN_PUBLISHED';
    const ok = window.confirm(
      `Delete ${orgLevel ? 'ORG-level ' : ''}workflow "${wf.name}"?\n\n` +
        'If it has run history it will be archived instead of permanently deleted.',
    );
    if (ok) deleteWf.mutate(wf.id);
  };

  const [scope, setScope] = useState<ScopeKey>('mine');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 200);
  const [triggerFilter, setTriggerFilter] = useState<TriggerFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tagFilter, setTagFilter] = useState<string>('');

  // ----- main list query ----------------------------------------------------
  const listQuery = useQuery<WorkflowListResponse>({
    queryKey: ['workflows', 'list', scope, debouncedSearch, tagFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('scope', scope);
      params.set('limit', '50');
      if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      if (tagFilter.trim()) params.set('tag', tagFilter.trim());
      return api<WorkflowListResponse>(`/api/v1/workflows?${params.toString()}`);
    },
  });

  // ----- scope rail counts: small list-of-1 per scope -----------------------
  // We don't have a dedicated count endpoint; the cheapest honest signal is
  // the page length from limit=50 + whether nextCursor is set. For the
  // currently-selected scope we reuse the main query.
  const scopeCountQueries = useQueries({
    queries: SCOPES.map((s) => ({
      queryKey: ['workflows', 'count', s.key],
      // Tiny page to estimate. We avoid limit=0 since Zod requires min(1).
      queryFn: () =>
        api<WorkflowListResponse>(`/api/v1/workflows?scope=${s.key}&limit=50`),
      // Refresh quietly; rail is informational.
      staleTime: 30_000,
    })),
  });

  const scopeEntries: ScopeRailEntry[] = SCOPES.map((s, idx) => {
    const r = scopeCountQueries[idx];
    const items = r?.data?.items ?? [];
    const next = r?.data?.nextCursor ?? null;
    const count = r?.isSuccess ? items.length + (next ? 1 : 0) : null;
    return {
      key: s.key,
      label: s.label,
      count,
      hint: next ? `${items.length}+` : undefined,
    };
  });

  // ----- create new workflow ------------------------------------------------
  const newWorkflowMut = useMutation<{ workflow: WorkflowMeta }>({
    mutationFn: () =>
      api<{ workflow: WorkflowMeta }>('/api/v1/workflows', {
        method: 'POST',
        body: JSON.stringify({
          name: 'Untitled workflow',
          tags: [],
          visibility: 'PRIVATE',
          attributionVisible: true,
          appliesToProducts: [],
          appliesToStates: [],
          // Server requires at least one trigger + one step.
          triggers: [{ type: 'event', event: 'loan.status_changed' }],
          steps: [
            {
              key: 'STEP_1',
              name: 'New step',
              category: 'NOTIFY',
              actions: [],
              cooldownHours: 0,
              isTerminal: false,
            },
          ],
          transitions: [],
        }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      navigate(`/workflows/${data.workflow.id}`);
    },
  });

  // ----- client-side filters ------------------------------------------------
  const items = listQuery.data?.items ?? [];
  const filteredItems = useMemo(() => {
    return items.filter((wf) => {
      if (statusFilter !== 'all' && wf.status !== statusFilter) return false;
      // Trigger filter is best-effort — meta doesn't carry triggers, so we
      // skip the filter when we have nothing to match against. Effectively
      // a Phase 2 follow-up; the chip is wired so server-side support can
      // land later without UI work.
      return true;
    });
  }, [items, statusFilter]);

  const handleRowClick = useCallback(
    (id: string) => navigate(`/workflows/${id}`),
    [navigate],
  );

  // ----- per-row trigger summary, lazy --------------------------------------
  // For the table column we'd ideally serve `triggers` summarized in the list
  // payload. Until then, we read activeInstanceCount + status badges and
  // skip the trigger summary detail (shown as "—"). triggerFilter therefore
  // does nothing in Phase 1; the chip is here so the UX shape ships now.
  void triggerFilter;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
      {/* SCOPE RAIL ----------------------------------------------------- */}
      <aside className="space-y-1">
        <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Scope
        </div>
        <ul className="space-y-0.5">
          {scopeEntries.map((s) => {
            const selected = s.key === scope;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => setScope(s.key)}
                  className={clsx(
                    'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                    selected
                      ? 'bg-brand-navy-700 text-white shadow-sm'
                      : 'text-slate-700 hover:bg-brand-navy-50',
                  )}
                >
                  <span className="truncate">{s.label}</span>
                  <span
                    className={clsx(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                      selected
                        ? 'bg-white/15 text-brand-cream'
                        : 'bg-slate-100 text-slate-500',
                    )}
                  >
                    {s.count === null ? '…' : s.hint ?? s.count}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* MAIN PANE ------------------------------------------------------ */}
      <section className="space-y-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-700">
              Workflows
            </p>
            <h1 className="mt-1 font-display text-3xl font-bold text-brand-navy-800">
              {scopeLabel(scope)}
            </h1>
          </div>
          <Button
            variant="gold"
            disabled={!perm.canEdit || newWorkflowMut.isPending}
            onClick={() => newWorkflowMut.mutate()}
          >
            {newWorkflowMut.isPending ? 'Creating…' : '+ New workflow'}
          </Button>
        </header>

        {/* Filter chips + search row */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            label="Trigger"
            value={triggerFilter === 'all' ? 'Any' : triggerFilter}
            options={[
              { value: 'all', label: 'Any' },
              { value: 'TIME', label: 'Time' },
              { value: 'EVENT', label: 'Event' },
              { value: 'CONDITION', label: 'Condition' },
            ]}
            onChange={(v) => setTriggerFilter(v as TriggerFilter)}
          />
          <FilterChip
            label="Status"
            value={statusFilter === 'all' ? 'Any' : statusFilter}
            options={[
              { value: 'all', label: 'Any' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ARCHIVED', label: 'Archived' },
            ]}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
          />
          <TagInput value={tagFilter} onChange={setTagFilter} />
          <span className="ml-auto text-xs text-slate-500">
            {filteredItems.length} {filteredItems.length === 1 ? 'result' : 'results'}
          </span>
        </div>

        <div className="relative">
          <SearchIcon />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workflows…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
          />
        </div>

        {/* TABLE -------------------------------------------------------- */}
        <div className="surface-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-brand-cream/60 text-left text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              <tr>
                <th className="w-10 px-3 py-2.5"></th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Trigger</th>
                <th className="px-3 py-2.5">Steps</th>
                <th className="px-3 py-2.5">Active</th>
                <th className="px-3 py-2.5">Updated</th>
                <th className="w-16 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!listQuery.isLoading && filteredItems.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500">
                    No workflows in this scope. Try a different filter or
                    click <span className="font-semibold text-brand-navy-700">+ New workflow</span> to start one.
                  </td>
                </tr>
              )}
              {filteredItems.map((wf) => (
                <tr
                  key={wf.id}
                  onClick={() => handleRowClick(wf.id)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-brand-navy-50"
                  style={{ height: 40 }}
                >
                  <td className="px-3 py-1">
                    <VisibilityGlyph visibility={wf.visibility} />
                  </td>
                  <td className="px-3 py-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-brand-navy-800">{wf.name}</span>
                      {wf.visibility === 'ADMIN_PUBLISHED' && (
                        <Badge tone="gold">★ ORG</Badge>
                      )}
                      {wf.status === 'DRAFT' && <Badge tone="neutral">Draft</Badge>}
                      {wf.status === 'ARCHIVED' && <Badge tone="neutral">Archived</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-1 text-xs text-slate-500">—</td>
                  <td className="px-3 py-1 text-xs text-slate-500">—</td>
                  <td className="px-3 py-1 text-xs text-slate-700">
                    {wf.activeInstanceCount}
                  </td>
                  <td className="px-3 py-1 text-xs text-slate-500">
                    {new Date(wf.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-1 text-right" onClick={(e) => e.stopPropagation()}>
                    {canDeleteRow(wf) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(wf)}
                        disabled={deleteWf.isPending}
                        className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        title="Delete workflow"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------

function scopeLabel(scope: ScopeKey): string {
  const found = SCOPES.find((s) => s.key === scope);
  return found?.label ?? 'Workflows';
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs shadow-sm">
      <span className="font-medium text-slate-500">{label}:</span>
      <select
        className="cursor-pointer border-0 bg-transparent text-brand-navy-700 focus:outline-none focus:ring-0"
        value={options.find((o) => o.label === value)?.value ?? options[0]?.value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TagInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs shadow-sm">
      <span className="font-medium text-slate-500">Tag:</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="any"
        className="w-20 border-0 bg-transparent text-brand-navy-700 placeholder:text-slate-400 focus:outline-none focus:ring-0"
      />
    </label>
  );
}

function VisibilityGlyph({ visibility }: { visibility: WorkflowMeta['visibility'] }) {
  if (visibility === 'ADMIN_PUBLISHED') {
    return <StatusDot tone="gold" />;
  }
  if (visibility === 'ORG_PUBLISHED') {
    return <StatusDot tone="info" />;
  }
  return <StatusDot tone="neutral" />;
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
    >
      <path
        fillRule="evenodd"
        d="M9 3a6 6 0 014.472 9.997l3.265 3.266a1 1 0 01-1.414 1.414l-3.266-3.265A6 6 0 119 3zm0 2a4 4 0 100 8 4 4 0 000-8z"
        clipRule="evenodd"
      />
    </svg>
  );
}
